import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  CheckCircle, 
  Building2, 
  CalendarDays, 
  FileText, 
  Upload, 
  Camera,
  Package,
  Clock
} from 'lucide-react';
import { useSuppliers } from '@/hooks/useSuppliers';
import { format } from 'date-fns';

interface OrderData {
  supplierCode: string;
  deliveryDate: string;
  invoicePhotos: string[];
  otherDocuments: string[];
  goodsPhotos: string[];
}

interface OrderSummaryCardProps {
  orderData: OrderData;
}

export const OrderSummaryCard = ({ orderData }: OrderSummaryCardProps) => {
  const { data: suppliers = [] } = useSuppliers();
  
  const selectedSupplier = suppliers.find(s => s.supplier_code === orderData.supplierCode);
  
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'EEEE, MMMM do, yyyy');
    } catch {
      return dateString;
    }
  };

  const summaryItems = [
    {
      label: 'Invoice Pages',
      count: orderData.invoicePhotos.length,
      icon: FileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      required: false
    },
    {
      label: 'Other Documents',
      count: orderData.otherDocuments.length,
      icon: Upload,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      required: false
    },
    {
      label: 'Product Photos',
      count: orderData.goodsPhotos.length,
      icon: Camera,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      required: false
    }
  ];

  const totalAttachments = orderData.invoicePhotos.length + 
                           orderData.otherDocuments.length + 
                           orderData.goodsPhotos.length;

  const requiredItemsComplete = true; // All document uploads are now optional

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="flex justify-center">
          {requiredItemsComplete ? (
            <CheckCircle className="w-12 h-12 text-green-600" />
          ) : (
            <Clock className="w-12 h-12 text-amber-600" />
          )}
        </div>
        <h2 className="text-2xl font-semibold">Review Order</h2>
        <p className="text-muted-foreground">
          Ready to create order. All document uploads are optional.
        </p>
      </div>

      {/* Order Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Order Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Supplier Info */}
          <div className="flex items-start justify-between">
            <div>
              <p className="font-medium">{selectedSupplier?.name || 'Unknown Supplier'}</p>
              <p className="text-sm text-muted-foreground">
                Code: {orderData.supplierCode}
              </p>
            </div>
            <Badge variant="outline">Supplier</Badge>
          </div>

          <Separator />

          {/* Delivery Date */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="font-medium">Delivery Date</p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(orderData.deliveryDate)}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Attachments Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Captured Documents & Photos
            <Badge variant="secondary" className="ml-auto">
              {totalAttachments} total
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {summaryItems.map((item) => (
              <div key={item.label} className="flex items-center justify-between p-3 rounded-lg border">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${item.bgColor}`}>
                    <item.icon className={`w-4 h-4 ${item.color}`} />
                  </div>
                  <div>
                    <p className="font-medium">{item.label}</p>
                    <p className="text-sm text-muted-foreground">
                      {item.required ? 'Required' : 'Optional'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{item.count}</p>
                  {item.count > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      Captured
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Preview Gallery */}
      {totalAttachments > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Preview Gallery</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-2">
              {/* Invoice Photos */}
              {orderData.invoicePhotos.slice(0, 4).map((photo, index) => (
                <div key={`invoice-${index}`} className="relative aspect-square rounded-lg overflow-hidden border">
                  <img 
                    src={photo} 
                    alt={`Invoice ${index + 1}`} 
                    className="w-full h-full object-cover" 
                  />
                  <div className="absolute top-1 left-1">
                    <Badge variant="secondary" className="text-xs">
                      I{index + 1}
                    </Badge>
                  </div>
                </div>
              ))}
              
              {/* Goods Photos */}
              {orderData.goodsPhotos.slice(0, 4 - orderData.invoicePhotos.length).map((photo, index) => (
                <div key={`goods-${index}`} className="relative aspect-square rounded-lg overflow-hidden border">
                  <img 
                    src={photo} 
                    alt={`Goods ${index + 1}`} 
                    className="w-full h-full object-cover" 
                  />
                  <div className="absolute top-1 left-1">
                    <Badge variant="secondary" className="text-xs">
                      G{index + 1}
                    </Badge>
                  </div>
                </div>
              ))}
              
              {/* Show more indicator */}
              {totalAttachments > 4 && (
                <div className="aspect-square rounded-lg border border-dashed border-muted-foreground/25 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-sm font-medium">+{totalAttachments - 4}</p>
                    <p className="text-xs text-muted-foreground">more</p>
                  </div>
                </div>
              )}
            </div>
            
            {totalAttachments > 0 && (
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>I</strong> = Invoice pages • <strong>G</strong> = Goods photos • 
                  Documents available after order creation
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Completion Status */}
      <Card className="border-green-200 bg-green-50/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <p className="font-medium">
                Ready to Create Order
              </p>
              <p className="text-sm text-muted-foreground">
                Order can be created with basic information only. Document uploads are optional.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};